import React, { useState } from 'react';
import { Modal, Button, Input, Form, Select, Row, Col, Space, message, Collapse } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Panel } = Collapse;

interface MoveToInventoryModalProps {
  visible: boolean;
  onClose: () => void;
  openMoveToInventoryModal: () => void;
}

const MoveToInventoryModal: React.FC<MoveToInventoryModalProps> = ({
                                                                     visible,
                                                                     onClose,
                                                                     openMoveToInventoryModal,
                                                                   }) => {
  const [inventoryCount, setInventoryCount] = useState(1);
  const [inventoryItems, setInventoryItems] = useState<any[]>([
    {
      productType: 'Mobile & Tablets',
      brand: 'Apple',
      model: '16 Pro Max',
      color: 'Blue',
      quantity: 1,
      imei1: '',
      imei2: '',
    },
  ]);

  // Add New Inventory Item
  const addInventoryItem = () => {
    setInventoryCount(inventoryCount + 1);
    setInventoryItems([
      ...inventoryItems,
      {
        productType: '',
        brand: '',
        model: '',
        color: '',
        quantity: 1,
        imei1: '',
        imei2: '',
      },
    ]);
  };

  // Handle Save
  const handleSave = () => {
    message.success('Inventory moved successfully');
    onClose(); // Close the modal after saving
  };

  // Handle Change in Inventory Item Fields
  const handleInventoryChange = (value: any, index: number, field: string) => {
    const newInventoryItems = [...inventoryItems];
    newInventoryItems[index][field] = value;
    setInventoryItems(newInventoryItems);
  };

  // Remove Inventory Item
  const removeInventoryItem = (index: number) => {
    const newInventoryItems = [...inventoryItems];
    newInventoryItems.splice(index, 1);
    setInventoryItems(newInventoryItems);
    setInventoryCount(inventoryCount - 1);
  };

  // Calculate Summary
  const calculateSummary = () => {
    const totalProducts = inventoryItems.length;
    const totalPurchasePrice = inventoryItems.reduce(
      (acc, item) => acc + (item.purchasePrice || 0) * (item.quantity || 1),
      0
    );
    const totalRetailPrice = inventoryItems.reduce(
      (acc, item) => acc + (item.retailPrice || 0) * (item.quantity || 1),
      0
    );
    return { totalProducts, totalPurchasePrice, totalRetailPrice };
  };

  const { totalProducts, totalPurchasePrice, totalRetailPrice } = calculateSummary();

  return (
    <Modal
      title="Move to Inventory"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1171}
      destroyOnClose
      centered
      style={{ height: '818px' }}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          {/* Basic Details Section */}
          <Col span={16}>
            <div className="flex flex-col h-[636px] overflow-hidden">
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <h3 className="form-section-heading">Basic Details</h3>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Customer ID" required className="form-item-tight">
                      <Input placeholder="Enter Customer ID" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Status" required className="form-item-tight">
                      <Select placeholder="Select Status">
                        <Option value="active">Active</Option>
                        <Option value="inactive">Inactive</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {[...Array(inventoryCount)].map((_, index) => (
                  <div key={index}>

                    <div className={'flex-row'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 className="form-section-heading">Inventory ({index + 1})</h3>
                      {index > 0 && (
                        <Button
                          icon={<DeleteOutlined />}
                          type="link"
                          onClick={() => removeInventoryItem(index)}
                          style={{ color: '#FF3B30', display: 'flex', alignItems: 'center', flexDirection: 'row-reverse', gap: '4px' }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Product Type" required className="form-item-tight">
                          <Select
                            value={inventoryItems[index].productType}
                            onChange={(value) => handleInventoryChange(value, index, 'productType')}
                            placeholder="Select Product Type"
                          >
                            <Option value="Mobile & Tablets">Mobile & Tablets</Option>
                            <Option value="Laptops">Laptops</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Brand" required className="form-item-tight">
                          <Select
                            value={inventoryItems[index].brand}
                            onChange={(value) => handleInventoryChange(value, index, 'brand')}
                            placeholder="Select Brand"
                          >
                            <Option value="Apple">Apple</Option>
                            <Option value="Samsung">Samsung</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Model" required className="form-item-tight">
                          <Input
                            value={inventoryItems[index].model}
                            onChange={(e) => handleInventoryChange(e.target.value, index, 'model')}
                            placeholder="Enter Model"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Quantity" required className="form-item-tight">
                          <Input
                            value={inventoryItems[index].quantity}
                            onChange={(e) => handleInventoryChange(Number(e.target.value), index, 'quantity')}
                            placeholder="Enter Quantity"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="IMEI (1)" required className="form-item-tight">
                          <Input
                            value={inventoryItems[index].imei1}
                            onChange={(e) => handleInventoryChange(e.target.value, index, 'imei1')}
                            placeholder="Enter IMEI (1)"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="IMEI (2)" required className="form-item-tight">
                          <Input
                            value={inventoryItems[index].imei2}
                            onChange={(e) => handleInventoryChange(e.target.value, index, 'imei2')}
                            placeholder="Enter IMEI (2)"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                  </div>
                ))}
              </div>
            </div>
          </Col>

          {/* Inventory Summary Section */}
          <Col span={8}>
            <div className="flex flex-col h-[636px] bg-[#FAFAFA] p-2 rounded-lg overflow-hidden">
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <h3 className="form-section-heading">Inventory Details</h3>

                <Collapse
                  defaultActiveKey={['1']}
                  bordered={false}
                  className="bg-white"
                  expandIconPosition="right"
                >
                  {inventoryItems.map((item, index) => (
                    <Panel header={`Inventory ${index + 1}`} key={index}>
                      {[
                        ['Product Type', item.productType],
                        ['Brand', item.brand],
                        ['Model', item.model],
                        ['Quantity', item.quantity],
                        ['IMEI (1)', item.imei1],
                        ['IMEI (2)', item.imei2],
                      ].map(([label, value], idx) => (
                        <Row gutter={16} key={idx}>
                          <Col span={12} style={{ textAlign: 'left' }}>
                            <strong>{label}:</strong>
                          </Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {value}
                          </Col>
                        </Row>
                      ))}
                    </Panel>
                  ))}
                </Collapse>
              </div>

              <div className="mt-auto">
                <h3 className="form-section-heading">Inventory Summary</h3>
                <Row>
                  <Col span={24}>
                    <div><strong>Total Products:</strong> {totalProducts}</div>
                    <div><strong>Total Purchase Price:</strong> {totalPurchasePrice}</div>
                    <div><strong>Total Retail Price:</strong> {totalRetailPrice}</div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {/* Footer Buttons */}
        <div className="flex justify-between mt-4">
          <Button
            onClick={addInventoryItem}
            icon={<PlusOutlined />}
            className="w-[191px] h-[40px] mb-4 text-[#BD273B] !border-[#BD273B] bg-white hover:bg-[#BD273B] hover:text-white hover:border-[#BD273B]"
          >
            Add New Inventory
          </Button>

          <Space>
            <Button onClick={onClose} className="h-[40px]">
              Cancel
            </Button>
            <Button type="primary" onClick={handleSave} className="h-[40px]">
              Move to Inventory
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default MoveToInventoryModal;
